// Status rules for the student Results page: one status per assessment card.

export const ASSESSMENT_ORDER = ['riasec', 'dass42', 'ryff', 'sdq'];

export const ASSESSMENT_META = {
  riasec: {
    name: 'Career interests',
    code: 'RIASEC',
    icon: 'bi-compass',
    description: 'A short chat about what you enjoy. It suggests courses and careers that match your interests.',
  },
  dass42: {
    name: 'Mood and stress',
    code: 'DASS-42',
    icon: 'bi-emoji-smile',
    description: "Questions about how you've been feeling over the past week. It helps your counselor know how to support you.",
  },
  ryff: {
    name: 'Well-being',
    code: 'Ryff scale',
    icon: 'bi-flower1',
    description: 'Questions about how you see yourself and your life right now.',
  },
  sdq: {
    name: 'Strengths and difficulties',
    code: 'SDQ',
    icon: 'bi-puzzle',
    description: 'A short questionnaire about your strengths and the things you find hard.',
  },
};

// Card statuses. The release reasons map onto the first three.
export const STATUS = {
  READY: 'ready',
  IN_REVIEW: 'in_review',
  NOT_SHARED: 'not_shared',
  LOADING: 'loading',
  ERROR: 'error',
  UNFINISHED: 'unfinished',
  NOT_STARTED: 'not_started',
  MISSED: 'missed',
  NOT_TAKEN: 'not_taken',
};

export const STATUS_BADGE = {
  [STATUS.READY]: { label: 'Ready', tone: 'success', icon: 'bi-check-circle' },
  [STATUS.IN_REVIEW]: { label: 'In review', tone: 'warning', icon: 'bi-hourglass-split' },
  [STATUS.NOT_SHARED]: { label: 'Not available', tone: 'neutral', icon: 'bi-eye-slash' },
  [STATUS.LOADING]: { label: 'Loading', tone: 'neutral', icon: 'bi-arrow-repeat' },
  [STATUS.ERROR]: { label: "Couldn't load", tone: 'danger', icon: 'bi-exclamation-circle' },
  [STATUS.UNFINISHED]: { label: 'Unfinished', tone: 'accent', icon: 'bi-pause-circle' },
  [STATUS.NOT_STARTED]: { label: 'Not started', tone: 'neutral', icon: 'bi-circle' },
  [STATUS.MISSED]: { label: 'Missed', tone: 'neutral', icon: 'bi-calendar-x' },
  [STATUS.NOT_TAKEN]: { label: 'Not taken', tone: 'neutral', icon: 'bi-dash-circle' },
};

const SUMMARY_WORDS = {
  [STATUS.READY]: 'ready',
  [STATUS.IN_REVIEW]: 'in review',
  [STATUS.NOT_SHARED]: 'not available',
  [STATUS.LOADING]: 'loading',
  [STATUS.ERROR]: "couldn't load",
  [STATUS.UNFINISHED]: 'unfinished',
  [STATUS.NOT_STARTED]: 'to take',
  [STATUS.MISSED]: 'missed',
  [STATUS.NOT_TAKEN]: 'not taken',
};

// Why a result is or isn't visible, as the API reports it.
export const RELEASE = { RELEASED: 'released', IN_REVIEW: 'in_review', NOT_SHARED: 'not_shared' };

/** Normalize an API answer (older ones only send results_released) to a release reason. */
export const releaseStatusOf = (data) => {
  if (!data) return null;
  if (data.release_status) return data.release_status;
  return data.results_released === false ? RELEASE.IN_REVIEW : RELEASE.RELEASED;
};

const isReleasedAttempt = (attempt) => releaseStatusOf(attempt) === RELEASE.RELEASED;

export const formatDate = (value, withTime = false) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit', hour12: true } : {}),
  });
};

const isPast = (value) => {
  if (!value) return false;
  const time = new Date(value).getTime();
  return !Number.isNaN(time) && time < Date.now();
};

/** Which assessments get a card: assigned ones this year, plus any with a result in the year. */
export const visibleAssessments = ({ assigned, attemptsByType, schoolYear, isCurrentYear }) => {
  const assignedList = Array.isArray(assigned) && assigned.length ? assigned : ['riasec'];
  return ASSESSMENT_ORDER.filter((type) => {
    const hasAttempt = (attemptsByType[type] || []).some((a) => a.school_year === schoolYear);
    return hasAttempt || (isCurrentYear && assignedList.includes(type));
  });
};

/**
 * One card's status and the facts its panel needs.
 * releaseState holds the release reason of the result being shown for each type.
 */
export const cardState = (type, ctx) => {
  const {
    attemptsByType, schoolYear, isCurrentYear, releaseState, fetchErrors,
    progress, deadlines, selectedAttemptId,
  } = ctx;
  const attempts = (attemptsByType[type] || []).filter((a) => a.school_year === schoolYear);
  const latest = attempts[0];
  const shown = attempts.find((a) => String(a.id) === String(selectedAttemptId)) || latest;

  if (fetchErrors[type]) return { status: STATUS.ERROR, attempts };

  if (attempts.length) {
    const reason = releaseState[type];
    const base = { attempts, latest, shown, completedAt: shown?.completed_at };
    if (!reason) return { ...base, status: STATUS.LOADING };
    if (reason === RELEASE.NOT_SHARED) return { ...base, status: STATUS.NOT_SHARED };
    if (reason === RELEASE.IN_REVIEW) {
      // A retake waiting for review should not hide an earlier result the student already has.
      const earlierReleased = attempts.find((a) => a.id !== shown?.id && isReleasedAttempt(a));
      return { ...base, status: STATUS.IN_REVIEW, earlierReleased };
    }
    return { ...base, status: STATUS.READY };
  }

  if (!isCurrentYear) return { status: STATUS.NOT_TAKEN, attempts };

  const session = progress?.[type];
  if (session && session.status !== 'completed' && Number(session.current) > 0) {
    return { status: STATUS.UNFINISHED, attempts, session };
  }

  const deadline = deadlines?.[type] || null;
  if (deadline && isPast(deadline)) return { status: STATUS.MISSED, attempts, deadline };
  return { status: STATUS.NOT_STARTED, attempts, deadline };
};

const STATUS_BY_RELEASE = {
  [RELEASE.RELEASED]: STATUS.READY,
  [RELEASE.IN_REVIEW]: STATUS.IN_REVIEW,
  [RELEASE.NOT_SHARED]: STATUS.NOT_SHARED,
};

/** What the card says: always the latest attempt, even while the panel shows an earlier one. */
export const cardSummary = (state) => {
  if (state.shown && state.latest && state.shown.id !== state.latest.id) {
    return {
      ...state,
      status: STATUS_BY_RELEASE[releaseStatusOf(state.latest)] || state.status,
      completedAt: state.latest.completed_at,
    };
  }
  return state;
};

export const cardMeta = (state) => {
  switch (state.status) {
    case STATUS.READY:
      return formatDate(state.completedAt);
    case STATUS.IN_REVIEW:
    case STATUS.NOT_SHARED:
      return state.completedAt ? `Submitted ${formatDate(state.completedAt)}` : '';
    case STATUS.ERROR:
      return 'Select to try again';
    case STATUS.UNFINISHED:
      return state.session?.total ? `${state.session.current} of ${state.session.total} answered` : 'In progress';
    case STATUS.NOT_STARTED:
      return state.deadline ? `Due ${formatDate(state.deadline)}` : 'Ready when you are';
    case STATUS.MISSED:
      return `Closed ${formatDate(state.deadline)}`;
    case STATUS.NOT_TAKEN:
      return 'No result this year';
    default:
      return '';
  }
};

export const summaryLine = (states) => {
  const counts = {};
  states.map(cardSummary).forEach((state) => {
    counts[state.status] = (counts[state.status] || 0) + 1;
  });
  return Object.keys(counts).map((status) => `${counts[status]} ${SUMMARY_WORDS[status]}`).join(' · ');
};

/** Pick the card to open first: a deep link, then new results, then anything needing attention. */
export const defaultSelection = (types, states, newTypes, requested) => {
  if (requested && types.includes(requested)) return requested;
  const firstWith = (test) => types.find((type) => test(states[type], type));
  return (
    firstWith((s, type) => s.status === STATUS.READY && newTypes.has(type))
    || firstWith((s) => s.status === STATUS.READY)
    || firstWith((s) => s.status === STATUS.IN_REVIEW)
    || firstWith((s) => s.status === STATUS.UNFINISHED)
    || firstWith((s) => s.status === STATUS.NOT_STARTED)
    || types[0]
    || null
  );
};

// "New" badges: results released since the student last opened them on this device.
const seenKey = (userId) => `anna:seen-results:${userId}`;

export const readSeenResults = (userId) => {
  try {
    const raw = localStorage.getItem(seenKey(userId));
    return raw === null ? null : new Set(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const markResultSeen = (userId, keys) => {
  try {
    const seen = readSeenResults(userId) || new Set();
    keys.forEach((key) => seen.add(key));
    localStorage.setItem(seenKey(userId), JSON.stringify([...seen].slice(-200)));
  } catch {
    // Storage can be unavailable (private mode); the badge is only a convenience.
  }
};

export const resultKey = (type, id) => `${type}:${id}`;

// N6: "New" from the server: when a released result (or its counselor note) was last shown or changed,
// against when the person last opened Results (ResultsSeen.seen_at, null on a first visit).
export const isNewSince = (attempt, seenAt) => {
  if (!attempt || !seenAt || !attempt.updated_at) return false;
  const changed = new Date(attempt.updated_at).getTime();
  const seen = new Date(seenAt).getTime();
  return !Number.isNaN(changed) && !Number.isNaN(seen) && changed > seen;
};

// N6: the session the student already has, for the strip under a result. A follow-up about this
// test comes first, else their next session; null when nothing is coming up.
// The booking topic codes (a student's own rows carry reason_code; older answers only the label)
const FOLLOW_UP_CODE = { riasec: 'riasec_followup', dass42: 'dass_followup', ryff: 'ryff_followup', sdq: 'sdq_followup' };
const FOLLOW_UP_LABEL = {
  riasec: 'RIASEC Follow-up',
  dass42: 'DASS-42 Follow-up',
  ryff: 'RYFF Follow-up',
  sdq: 'SDQ Follow-up',
};
const ACTIVE_SESSION = ['confirmed', 'pending', 'reschedule_requested'];
const isFollowUpFor = (type, row) => (row.reason_code ? row.reason_code === FOLLOW_UP_CODE[type] : row.reason === FOLLOW_UP_LABEL[type]);

export const upcomingSessionFor = (type, appointments, now = new Date()) => {
  const upcoming = (Array.isArray(appointments) ? appointments : [])
    .filter((row) => ACTIVE_SESSION.includes(row.status) && row.counselor && row.start_at)
    .filter((row) => new Date(row.end_at || row.start_at).getTime() > now.getTime())
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  const followUp = upcoming.find((row) => isFollowUpFor(type, row));
  const row = followUp || upcoming[0];
  if (!row) return null;
  return { ...row, aboutThisTest: Boolean(followUp) };
};

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "today", "tomorrow", "on Tue" (within the week) or "on Nov 12". */
export const sessionDayWords = (startAt, now = new Date()) => {
  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) return '';
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  if (sameDay(start, now)) return 'today';
  if (sameDay(start, tomorrow)) return 'tomorrow';
  const days = (start.getTime() - now.getTime()) / 86400000;
  if (days < 6) return `on ${start.toLocaleDateString('en-US', { weekday: 'short' })}`;
  return `on ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

/** "Nov 3, 2:00 PM". */
export const sessionWhen = (startAt) => {
  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) return '';
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
};
